import throttle from 'lodash.throttle'
import React from 'react'

import { ColorVariant } from '../../components'
import { TableAlign, TableHead, TableHeader } from '../../components/Table'
import {
  FinancialsTableContextProvider,
  FinancialsWidget,
  getIsCompact,
  getYoYData,
  useFinancialsTableContext,
} from '../../components/financials-table'
import { useCachedLoaderData } from '../../hooks/useCachedLoaderData'
import { QuoteFinancialsTabMenu } from '../quote/components/QuoteFinancialsTabMenu'
import { QuoteStickySubMenu } from '../quote/components/QuoteStickySubMenu'
import DividendsHeader from './DividendsHeader'
import { DividendsPayoutHistory } from './DividendsPayoutHistory'
import WidgetSettingsSection from './WidgetSettingsSection'
import { DividendsChart } from './charts/DividendsChart'
import { BAR_WIDTH, DividendValueProperty } from './constants'
import { DividendData, DividendDataEnum, DividendSettings, DividendsDataType } from './types'
import { useTableColumns } from './useTableColumns'

const dividendsAnnualDataTemplate: Array<Pick<DividendsDataType, 'id' | 'label' | 'color' | 'isSelected'>> = [
  { label: 'Dividends per share (FY)', id: DividendValueProperty.Amount, color: ColorVariant.teal, isSelected: true },
  { label: 'Dividend yield (FY) %', id: DividendValueProperty.Yield, color: ColorVariant.green, isSelected: true },
  { label: 'Payout ratio (FY) %', id: DividendValueProperty.Payout, color: ColorVariant.purple, isSelected: false },
]

function mapData(data: DividendData) {
  const dividendsArray = dividendsAnnualDataTemplate.map((dividendCategory) => ({
    ...dividendCategory,
    values: data.dividendsAnnualData.map((annualData) => ({
      year: annualData.FiscalPeriod.substring(0, 4),
      value: annualData[dividendCategory.id],
      isEstimate: annualData.Estimate,
    })),
  }))

  return dividendsArray.map((dividendCategory) => {
    const yoyData = getYoYData(dividendCategory.values)

    return {
      ...dividendCategory,
      subData: [
        {
          id: DividendDataEnum.yoyChange,
          label: 'YoY Growth',
          values: dividendCategory.values.map((anualDividend) => ({
            ...anualDividend,
            value: yoyData.change[anualDividend.year] ?? null,
          })),
        },
        {
          id: DividendDataEnum.yoyChangePct,
          label: 'YoY Growth %',
          values: dividendCategory.values.map((anualDividend) => ({
            ...anualDividend,
            value: yoyData.changePct[anualDividend.year] ?? null,
          })),
        },
      ].filter(Boolean),
    }
  })
}

function DividendsComponent() {
  const { data } = useCachedLoaderData<DividendData | null>()
  const { settings } = useFinancialsTableContext<DividendSettings>()
  const [annualData, setAnnualData] = React.useState(() => (data ? mapData(data) : []))
  const yearKeys = React.useMemo(
    () =>
      annualData
        .flatMap(({ values }) => values.map(({ year }) => year))
        .filter((year, index, arr) => arr.indexOf(year) === index),
    [annualData]
  )
  const [isCompact, setIsCompact] = React.useState(getIsCompact(yearKeys.length, BAR_WIDTH))
  const { columns, subColumns } = useTableColumns(yearKeys, isCompact)

  React.useEffect(() => {
    // Remove min-height from react root element which is set on BE because of CLS
    document.getElementById('root')?.style.removeProperty('min-height')

    const onResize = throttle(() => {
      setIsCompact(getIsCompact(yearKeys.length, BAR_WIDTH))
    }, 100)

    onResize()

    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [yearKeys])

  const onVisibilityChange = (id: string, isSelected: boolean) => {
    setAnnualData(
      annualData.map((item) =>
        item.id === id
          ? {
              ...item,
              isSelected,
            }
          : item
      )
    )
  }

  const hasData = !!data && (data?.dividendsAnnualData?.length ?? 0) > 0 && (data?.dividendsData?.length ?? 0) > 0

  return (
    <>
      <QuoteStickySubMenu
        leftContent={<QuoteFinancialsTabMenu />}
        rightContent={hasData ? <WidgetSettingsSection /> : undefined}
      />

      {hasData ? (
        <div
          className="fv-container mb-4 mt-2"
          style={{
            minWidth: isCompact ? yearKeys.length * 70 + 200 : undefined,
          }}
        >
          <DividendsHeader
            lastClose={data.lastClose}
            dividendExDate={data.dividendExDate}
            dividendTTM={data.dividendTTM}
            dividendEstimate={data.dividendEstimate}
            dividendsData={data.dividendsData}
          />

          <FinancialsWidget
            data={annualData.map((revenue) => ({
              ...revenue,
              subData: revenue.subData.filter(({ id }) => settings[id]),
            }))}
            tableHead={
              <TableHead>
                <TableHeader align={TableAlign.Left} />

                {columns.map((column) => (
                  <TableHeader key={column.name} align={column.align} className={column.className}>
                    {column.name}
                  </TableHeader>
                ))}
              </TableHead>
            }
            columns={columns}
            subColumns={subColumns}
            onVisibilityChange={onVisibilityChange}
          >
            <DividendsChart isCompact={isCompact} annualData={annualData} />
          </FinancialsWidget>

          <DividendsPayoutHistory dividends={data.dividendsData} />
        </div>
      ) : (
        <div className="fv-container flex h-60 items-center justify-center text-muted">
          Dividends are not available for this stock.
        </div>
      )}
    </>
  )
}

const COOKIE_KEY = 'dividendsSettings'
const DEFAULT_SETTINGS: DividendSettings = {
  [DividendDataEnum.yoyChange]: false,
  [DividendDataEnum.yoyChangePct]: false,
}

export default function Dividends() {
  return (
    <FinancialsTableContextProvider defaultSettings={DEFAULT_SETTINGS} cookieKey={COOKIE_KEY}>
      <DividendsComponent />
    </FinancialsTableContextProvider>
  )
}
